import { cn } from "@/lib/utils";
import { MaybeLink } from "@/v2/layout/MaybeLink";
import type { FigureSubStepId } from "@/v2/stage-vocabulary";
import { nextStep, previousStep } from "./figure-steps";
import { stepDestination } from "@/v2/stage-vocabulary";

const STEP_LINK = "rounded-md border px-3.5 py-2 text-[13px] font-semibold";

/**
 * The way from one figure step to the next or the previous, at the foot of a screen.
 *
 * The next link reads "Continue to <step> →" and the previous one "← <step>", as v0.26 draws
 * every figure step's links.
 *
 * The classes are given once for both states, so the unreachable one cannot drift
 * from the live one. It is unreachable when the step it points at is not built, or
 * when the screen says the attorney is not ready to leave it. What that reads as is
 * `MaybeLink`'s to decide, not this screen's: it used to stay in the tab order and
 * then refuse to move, which is worse for a keyboard than not being there.
 */
export function StepLink({
  taskId,
  from,
  direction,
  ready = true,
  primary = false,
}: {
  taskId: string | undefined;
  from: FigureSubStepId;
  direction: "next" | "previous";
  /** Whether the screen is willing to let the attorney move on; ignored going back. */
  ready?: boolean;
  primary?: boolean;
}) {
  const step = direction === "next" ? nextStep(from) : previousStep(from);
  if (step === undefined) return null;
  const to = stepDestination(step, taskId, "figures");
  const live = to !== undefined && (direction === "previous" || ready);
  return (
    <MaybeLink
      to={live ? to : undefined}
      className={cn(
        STEP_LINK,
        primary
          ? "border-transparent bg-primary text-primary-foreground"
          : "border-border bg-card",
        // Only the reachable one answers the pointer. Held back it used to carry
        // `pointer-events-none`, which suppressed the hover as a side effect;
        // saying which states brighten is the same result asked for directly.
        live && primary && "hover:brightness-95",
        !live && "cursor-not-allowed opacity-45",
      )}
    >
      {/* The arrow says which way for the eye; a reader hears the words alone. */}
      {direction === "next" ? (
        <>
          {`Continue to ${step.label} `}
          <span aria-hidden="true">→</span>
        </>
      ) : (
        <>
          <span aria-hidden="true">←</span>
          {` ${step.label}`}
        </>
      )}
    </MaybeLink>
  );
}
